import AppIconButton from "../form/AppIconButton";
import React, { useImperativeHandle, useState } from "react";
import { Alert, AlertTitle, Snackbar, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation } from "react-i18next";
import { parseBackendNotificationDetails } from "../../utils/notification";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

const colorToSeverity = (color: string) => {
  if (color === "error") return "error";
  if (color === "warning") return "warning";
  if (color === "success") return "success";
  return "info";
};

const iconForColor = (color: string) => {
  if (color === "error") return <ErrorOutlineIcon fontSize="inherit" />;
  if (color === "warning") return <WarningAmberIcon fontSize="inherit" />;
  if (color === "success") return <CheckCircleOutlineIcon fontSize="inherit" />;
  return <InfoOutlinedIcon fontSize="inherit" />;
};

const Notification = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const typeToColor: Record<string, string> = {
    error: "error",
    warning: "warning",
    success: "success",
    info: "info",
  };

  const normalizeOptions = (opts: any) => {
    const normalized = { ...(opts || {}) };
    const normalizedType = String(normalized.type || "").toLowerCase();
    const backendDetails = parseBackendNotificationDetails(
      normalized.raw || normalized.response || normalized.data
    );

    if (!normalized.color && normalizedType && typeToColor[normalizedType]) {
      normalized.color = typeToColor[normalizedType];
    }
    if (!normalized.content && normalized.message) {
      normalized.content = normalized.message;
    }
    if (!normalized.reason && backendDetails.reason) {
      normalized.reason = backendDetails.reason;
    }
    if (!normalized.content && backendDetails.message) {
      normalized.content = backendDetails.message;
    }
    if (!normalized.info && backendDetails.info) {
      normalized.info = backendDetails.info;
    }
    if (!normalized.title && backendDetails.errorCode && backendDetails.reason) {
      normalized.title = `${backendDetails.reason} (${backendDetails.errorCode})`;
    }
    if (!normalized.color && String(normalized.title || "").toLowerCase().includes("error")) {
      normalized.color = "error";
    }
    if (!normalized.title) {
      normalized.title =
        normalized.color === "success" ? t("notification.successTitle") : t("notification.defaultTitle");
    }

    return normalized;
  };

  useImperativeHandle(ref, () => ({
    show(opts: any) {
      setSnackOptions(normalizeOptions(opts));
      setInfoSB(true);
    },
  }));

  const [infoSB, setInfoSB] = useState(false);
  const [snackOptions, setSnackOptions] = useState({
    title: "",
    content: "",
    reason: "",
    color: "info",
  });

  const closeInfoSB = () => setInfoSB(false);
  const severity = colorToSeverity(snackOptions.color || "info");

  return (
    <Snackbar
      open={infoSB}
      onClose={closeInfoSB}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      autoHideDuration={5000}
    >
      <Alert
        severity={severity}
        icon={iconForColor(snackOptions.color || "info")}
        variant="filled"
        sx={custom.components.notification.alert}
        action={
          <AppIconButton size="small" color="inherit" onClick={closeInfoSB} aria-label={t("common.close")}>
            <CloseIcon fontSize="small" />
          </AppIconButton>
        }
      >
        <AlertTitle sx={custom.components.notification.alertTitle(!!snackOptions.content)}>
          {snackOptions.title || t("notification.defaultTitle")}
        </AlertTitle>
        {snackOptions.content ? (
          <Typography variant="body2" component="div">
            {snackOptions.content}
          </Typography>
        ) : null}
        {snackOptions.reason ? (
          <Typography variant="caption" component="div" sx={custom.components.notification.reason}>
            {snackOptions.reason}
          </Typography>
        ) : null}
      </Alert>
    </Snackbar>
  );
});

export default Notification;
